import React, { useState, useEffect } from 'react';
import { InvoicePaymentsDto, InvoicingDetailDto } from '../../../types';
import { Card, CardBody, CardTitle, Table, Modal, ModalHeader, ModalBody } from 'reactstrap';
import moment from 'moment';
import { InvoiceStatus, PaymentType } from '../../../common/Data';
import InvoicePaymentForm from './InvoicePaymentForm';
import { Button } from '../../../components';
import { invoiceService } from '../../../services/InvoiceService';
import { toast } from 'react-toastify';

interface InvoicePaymentsProps {
    payments: InvoicePaymentsDto[];
    amountPaid: number;
    invoice: InvoicingDetailDto | undefined;
    onSave: () => void;
}

const InvoicePayments = (props: InvoicePaymentsProps) => {
    const [payments, setPayments] = useState<InvoicePaymentsDto[]>([]);
    const [addingPayment, setAddingPayment] = useState(false);
    const [isRefund, setIsRefund] = useState(false);

    // Sort
    useEffect(() => {
        setPayments(props.payments.sort((a, b) => moment(a.createDate).diff(moment(b.createDate))));
    }, [setPayments, props.payments]);

    const paymentsUpdated = () => {
        setAddingPayment(false);
        props.onSave();
    };

    const deletePayment = (payment: InvoicePaymentsDto) => {
        const toastId = toast.info('Deleting payment...');

        payment.isDeleted = true;

        invoiceService
            .saveInvoicePayment(payment)
            .then(() => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Payment deleted successfuly',
                });
                props.onSave();
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Error deleting payment',
                });
            });
    };

    return (
        <>
            <Card>
                <CardBody>
                    <CardTitle>
                        <h3>Payments</h3>
                    </CardTitle>
                    <Table striped responsive>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Paid By</th>
                                <th>Type</th>
                                <th>Amount</th>
                                <th>Transaction/Reference #</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {payments
                                .filter((x) => x.isDeleted === false)
                                .map((payment, i) => (
                                    <tr
                                        key={i}
                                        style={{ backgroundColor: payment.isConfirmed ? undefined : '#fcf3c4' }}
                                    >
                                        <td>{moment(payment.paymentDate).format('YYYY-MM-DD')}</td>
                                        <td>{`${payment.paidBy || ``}`}</td>
                                        <td>{PaymentType[payment.paymentType]}</td>
                                        <td>${payment.paymentAmount?.toFixed(2)}</td>
                                        <td>
                                            {payment.transactionId ? payment.transactionId : payment.referenceNumber}
                                        </td>
                                        <td>
                                            {!payment.transactionId && !payment.receiptUrl ? (
                                                <Button onClick={() => deletePayment(payment)}>Delete</Button>
                                            ) : (
                                                <a href={payment.receiptUrl || undefined}>
                                                    {payment.receiptUrl ? 'Receipt' : ''}
                                                </a>
                                            )}
                                            {!payment.isConfirmed && 'Processing'}
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </Table>
                    {!props.invoice?.isDeleted && (
                        <div style={{ paddingTop: '24px', display: 'flex' }}>
                            {(props.invoice?.dueAmount || 0) > 0 && (
                                <div className="mr-sm-2">
                                    <Button
                                        onClick={() => {
                                            setAddingPayment(true);
                                            setIsRefund(false);
                                        }}
                                    >
                                        Add Payment
                                    </Button>
                                </div>
                            )}
                            {props.invoice?.status !== InvoiceStatus.Unpaid && (
                                <div className="mr-sm-2">
                                    <Button
                                        color="danger"
                                        onClick={() => {
                                            setAddingPayment(true);
                                            setIsRefund(true);
                                        }}
                                    >
                                        Add Refund
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </CardBody>
            </Card>

            {props.invoice && (
                <Modal isOpen={addingPayment} toggle={() => setAddingPayment(!addingPayment)} size="lg">
                    <ModalHeader toggle={() => setAddingPayment(!addingPayment)}>
                        {isRefund ? 'Apply Refund' : 'Add Payment'}
                    </ModalHeader>
                    <ModalBody>
                        <InvoicePaymentForm
                            invoice={props.invoice}
                            onSave={() => paymentsUpdated()}
                            onCancel={() => setAddingPayment(false)}
                            isRefund={isRefund}
                        />
                    </ModalBody>
                </Modal>
            )}
        </>
    );
};

export default InvoicePayments;
